import React, { useCallback, useEffect, useRef, useState } from "react";
import type { DragEndEvent } from "@dnd-kit/core";
import { DndContext, PointerSensor, useSensor } from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy as sortingStrategy,
  SortableContext,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Image, Upload as UploadOriginal } from "antd";
import { RcFile, UploadFile } from "antd/lib/upload/interface";

import { ImageAdd } from "components/Icons/ImageAdd";
import {
  FILE_UPLOAD_IMAGES_ALLOWED_MIME_TYPES,
  FILE_UPLOAD_IMAGES_MAX_FILE_SIZE
} from "../../config/common";
import { UploadChangeParam } from "antd/es/upload/interface";
import styled from "styled-components/macro";
import { DefaultFont } from "../../styles/fonts";
import {
  getFileUploadImageAllowedExtensionsLabel,
  getMaxImageUploadSizeLabel
} from "../../utils/uploadAvatar";
import { THousePhoto } from "../Wizard/interfaces";
import { LoadingIndicator } from "../LoadingIndicator";
import { useNotifier } from "../../context/ContextNotifier";
import { rotateImage } from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { usePromisesLoader } from "../../utils/usePromiseLoader";
import {
  ImageExplanation,
  ImageExplanationTitle,
  ImageExplanationList
} from "./styles";
const Root = styled.div`
  position: relative;
`;
const InitialUploadMessage = styled.div``;
const Message = styled.div`
  ${DefaultFont};
  font-size: 16px;
  margin-bottom: 4px;

  & > span {
    color: ${(p) => p.theme.colors.primary};
    font-weight: 500;
  }
`;
const AllowedTypes = styled.div`
  color: ${(p) => p.theme.colors.midGrey};
  font-weight: 500;
`;

const Upload = styled(UploadOriginal)<{ $imagePresent: boolean }>`
  .ant-upload-select {
    grid-column-start: span 3;
    grid-row-start: 1;
    width: 100% !important;
    margin-bottom: 32px !important;
    padding: 32px;
    height: auto !important;
  }

  .ant-upload-list {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    //justify-content: space-between;
    gap: 16px;

    &:before {
      display: none;
    }
  }

  .ant-upload-list-item-container {
    width: 200px !important;
    height: 200px !important;
    margin-block: 0 !important;
    margin-inline: 0 !important;
  }

  @media (max-width: 768px) {
    .ant-upload-select {
      grid-column-start: span 1;
    }

    .ant-upload-list {
      grid-template-columns: 1fr;
      justify-items: center;
    }
  }
`;

const mimeTypes = FILE_UPLOAD_IMAGES_ALLOWED_MIME_TYPES.split(",").map((t) =>
  t.trim()
);

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.65);
  z-index: 100;
`;

const ImagePreviewModal = styled(Image.PreviewGroup)``;
const PreviewModalToolbarWrapper = styled.div`
  .ant-image-preview-operations-operation-flipX,
  .ant-image-preview-operations-operation-flipY {
    display: none;
  }
`;

interface DraggableUploadListItemProps {
  originNode: React.ReactElement<
    any,
    string | React.JSXElementConstructor<any>
  >;
  file: UploadFile<any>;
  removingFilesIds: string[];
  disabled?: boolean;
}

const DraggableUploadListItem = ({
  originNode,
  file,
  removingFilesIds,
  disabled
}: DraggableUploadListItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: file.uid
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: disabled ? "default" : "move",
    position: "relative"
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      // prevent preview event when drag end
      className={isDragging ? "is-dragging" : ""}
      {...attributes}
      {...listeners}
    >
      {/* hide error tooltip when dragging */}
      {file.status === "error" && isDragging
        ? originNode.props.children
        : originNode}
      {removingFilesIds.includes(file.uid) ? (
        <LoaderOverlay>
          <LoadingIndicator />
        </LoaderOverlay>
      ) : null}
    </div>
  );
};

export const MultipleImagesUploader: React.FC<{
  onImagesChanged?: (file: THousePhoto[]) => void;
  readonly?: boolean;
  images: THousePhoto[];
  uploadImage: (file: File, order: number) => Promise<THousePhoto>;
  deleteImage: (fileName: string) => Promise<any>;
  reorderImages: (
    order: { file_name: string; order: number }[]
  ) => Promise<any>;
}> = ({
  images,
  readonly,
  uploadImage,
  deleteImage,
  reorderImages,
  onImagesChanged
}) => {
  const [fileList, setFileList] = useState<UploadFile<RcFile>[]>(
    images
      .sort((a, b) => {
        return a.order < b.order ? -1 : 1;
      })
      .map((p) => ({
        uid: "initial-id:" + p.file_name,
        url: p.url,
        name: `fileName`,
        fileName: p.file_name,
        status: "done"
      }))
  );
  const { t } = useTranslation("wizard");
  const [removingFilesIds, setRemovingFilesIds] = useState<string[]>([]);
  const [previewFile, setPreviewFile] = useState<{
    file: UploadFile<RcFile>;
    rotation: number;
  } | null>(null);
  const currentlyUploadingFileRef = useRef<UploadFile<RcFile> | null>();
  const notifier = useNotifier();
  const onImagesChangedRef = useRef(onImagesChanged);
  const uploadImageRef = useRef(uploadImage);
  const reorderImagesRef = useRef(reorderImages);
  const deleteImageRef = useRef(deleteImage);
  const { isLoading, watchPromise } = usePromisesLoader();

  const uploadFile = useCallback(
    async (file: UploadFile<RcFile>, order: number) => {
      setFileList((prev) => {
        return prev.map((f) => {
          if (f.uid === file.uid) {
            return {
              ...f,
              status: "uploading",
              percent: 33
            };
          }
          return f;
        });
      });
      try {
        const uploadedImage = await uploadImageRef.current(
          file.originFileObj!,
          order
        );
        setFileList((prev) => {
          const updatedFiles = prev.map((f) => {
            if (f.uid === file.uid) {
              return {
                ...f,
                status: "done",
                percent: 100,
                url: uploadedImage.url,
                fileName: uploadedImage.file_name,
                // originFileObj and thumbUrl should be set to null to fix problem with wrong preview
                // on preview should be cropped image from backend
                originFileObj: null,
                thumbUrl: null
              };
            }
            return f;
          }) as UploadFile<RcFile>[];

          onImagesChangedRef.current?.(
            updatedFiles.map((f, index) => ({
              url: f.url,
              file_name: f.fileName!,
              order: index
            }))
          );

          return updatedFiles;
        });
      } catch (e) {
        setFileList((prev) => {
          return prev.map((f) => {
            if (f.uid === file.uid) {
              return {
                ...f,
                status: "error",
                percent: 100
              };
            }
            return f;
          }) as UploadFile<RcFile>[];
        });
      }

      currentlyUploadingFileRef.current = null;
    },
    []
  );

  const checkUploadQueue = useCallback(
    (fileList: UploadFile<RcFile>[]) => {
      if (currentlyUploadingFileRef.current) {
        return;
      }

      for (const file of fileList) {
        if (file.status === "uploading") {
          currentlyUploadingFileRef.current = file;
          uploadFile(file, fileList.indexOf(file));
          break;
        }
      }
    },
    [uploadFile]
  );

  useEffect(() => {
    checkUploadQueue(fileList);
  }, [fileList, checkUploadQueue]);

  const handleChange = async (info: UploadChangeParam<UploadFile>) => {
    const file = info.file;
    const updatedFile = fileList.find((f) => f.uid === info.file.uid);
    if (updatedFile) {
      setFileList((prev) => prev.map((f) => (f.uid === file.uid ? file : f)));
    } else {
      const formatIsSupported = mimeTypes.includes(file.originFileObj?.type!);
      if (!formatIsSupported) {
        notifier.error({
          message: `Image format is not supported. Supported formats are ${getFileUploadImageAllowedExtensionsLabel()}`
        });
        return;
      }
      const sizeIsSupported =
        file.originFileObj?.size! <= FILE_UPLOAD_IMAGES_MAX_FILE_SIZE;
      if (!sizeIsSupported) {
        notifier.error({
          message: `Image size is too big. Maximum allowed size is ${getMaxImageUploadSizeLabel()}`
        });
        return;
      }
      setFileList((prev) => [...prev, file]);
    }
  };

  const handlePreview = async (file: UploadFile) => {
    setPreviewFile({ file, rotation: 0 });
  };

  const handleDelete = useCallback(
    async (file: UploadFile): Promise<boolean> => {
      setRemovingFilesIds((prev) => [...prev, file.uid]);

      try {
        let imageWasDeletedOnBe = false;
        if (file.status === "done") {
          // it means it was uploaded without error
          await deleteImageRef.current(file.fileName!);
          imageWasDeletedOnBe = true;
        }

        setFileList((prev) => {
          const updatedFiles = prev.filter((p) => p.uid !== file.uid);

          // this is anti-pattern to call side-effects in setState function
          // but it is easy to access latest fileList
          onImagesChangedRef.current?.(
            updatedFiles.map((f, index) => ({
              url: f.url,
              file_name: f.fileName!,
              order: index
            }))
          );

          if (imageWasDeletedOnBe) {
            watchPromise(
              reorderImagesRef.current(
                updatedFiles.map((f, index) => {
                  return {
                    file_name: f.fileName!,
                    order: index
                  };
                })
              )
            );
          }

          return updatedFiles;
        });
        return true;
      } finally {
        setRemovingFilesIds((prev) => prev.filter((id) => id !== file.uid));
      }
    },
    [watchPromise]
  );

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 }
  });

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (active.id !== over?.id) {
        const activeIndex = fileList.findIndex((i) => i.uid === active.id);
        const overIndex = fileList.findIndex((i) => i.uid === over?.id);
        const updatedList = arrayMove([...fileList], activeIndex, overIndex);
        setFileList(updatedList);
        onImagesChangedRef.current?.(
          updatedList.map((f, index) => ({
            url: f.url,
            file_name: f.fileName!,
            order: index
          }))
        );

        watchPromise(
          reorderImagesRef.current(
            updatedList.map((f, index) => {
              return {
                file_name: f.fileName!,
                order: index
              };
            })
          )
        );
      }
    },
    [fileList, watchPromise]
  );

  return (
    <Root>
      <DndContext sensors={[sensor]} onDragEnd={handleDragEnd}>
        <SortableContext
          items={fileList.map((i) => i.uid)}
          strategy={sortingStrategy}
          disabled={readonly}
        >
          <Upload
            listType="picture-card"
            accept={FILE_UPLOAD_IMAGES_ALLOWED_MIME_TYPES}
            customRequest={() => {}}
            fileList={fileList}
            onChange={handleChange}
            onPreview={handlePreview}
            onRemove={handleDelete}
            disabled={readonly}
            $imagePresent={fileList.length !== 0}
            multiple={true}
            itemRender={(originNode, file) => (
              <DraggableUploadListItem
                originNode={originNode}
                file={file}
                removingFilesIds={removingFilesIds}
                disabled={readonly}
              />
            )}
          >
            {!readonly ? (
              <InitialUploadMessage>
                <ImageAdd />
                <Message>
                  <span>{t("step.Avatar.upload")}</span> {t("step.Avatar.drag")}
                </Message>
                <AllowedTypes>
                  {getFileUploadImageAllowedExtensionsLabel()}{" "}
                  {t("step.Avatar.upTo")} {getMaxImageUploadSizeLabel()}
                </AllowedTypes>
              </InitialUploadMessage>
            ) : null}
          </Upload>
        </SortableContext>
      </DndContext>
      {previewFile ? (
        <div style={{ display: "none" }}>
          <ImagePreviewModal
            preview={{
              visible: true,
              movable: false,
              toolbarRender: (originalNode) => {
                return (
                  <PreviewModalToolbarWrapper>
                    {originalNode}
                  </PreviewModalToolbarWrapper>
                );
              },
              onVisibleChange: () => {
                watchPromise(
                  (async () => {
                    setPreviewFile(null);

                    const finalRotation = previewFile?.rotation % 360;

                    if (finalRotation === 0 || !previewFile.file.url) {
                      setPreviewFile(null);
                      return;
                    }

                    const rotatedImage = await rotateImage(
                      previewFile.file.url,
                      finalRotation
                    );

                    if (!rotatedImage) {
                      setPreviewFile(null);
                      notifier.error({
                        message: "Cannot rotate image. Try again"
                      });
                      return;
                    }
                    try {
                      await deleteImageRef.current(previewFile?.file.fileName!);

                      const uploadedImage = await uploadImageRef.current(
                        rotatedImage,
                        fileList.findIndex(
                          (f) => f.uid === previewFile.file.uid
                        )
                      );

                      setFileList((prev) => {
                        const updatedFiles = prev.map((f) => {
                          if (f.uid === previewFile?.file.uid) {
                            return {
                              ...f,
                              url: uploadedImage.url,
                              fileName: uploadedImage.file_name
                            };
                          }
                          return f;
                        }) as UploadFile<RcFile>[];

                        onImagesChangedRef.current?.(
                          updatedFiles.map((f, index) => ({
                            url: f.url,
                            file_name: f.fileName!,
                            order: index
                          }))
                        );

                        return updatedFiles;
                      });
                    } catch {
                      notifier.error({
                        message: "Cannot rotate image. Try again"
                      });
                    }
                  })()
                );
              },
              onTransform: (info) => {
                setPreviewFile((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    file: prev.file,
                    rotation: info.transform.rotate
                  };
                });
              }
            }}
          >
            <Image src={previewFile.file.url} />
          </ImagePreviewModal>
        </div>
      ) : null}
      {isLoading ||
      removingFilesIds.length > 0 ||
      fileList.some((f) => f.status === "uploading") ? (
        <LoaderOverlay>
          <LoadingIndicator />
        </LoaderOverlay>
      ) : null}
      <ImageExplanation>
        <ImageExplanationTitle>
          {t("imageUploading.title")}
        </ImageExplanationTitle>
        <ImageExplanationList>
          <li>{t("imageUploading.tip1")}</li>
          <li>{t("imageUploading.tip2")}</li>
          <li>{t("imageUploading.tip3")}</li>
          <li>{t("imageUploading.tip4")}</li>
        </ImageExplanationList>
      </ImageExplanation>
    </Root>
  );
};
// How to make your place stand out: 1)
